import { useEffect, useState } from 'react';
import { Alert } from 'antd';
import {
    WriteSmartReplyCallback,
    WriteSmartReplyQuery,
    EditSmartReplyQuery,
    CUSTOM_PROMPT_KEY,
} from '@copilot/common/hooks/smartReply/smartReplyTypes';
import { StatusIndicator } from '@copilot/common/components/statusIndicator/statusIndicator';
import { delay, isUndefined } from 'lodash';
import styled from 'styled-components';
import { useSmartReplyPromptPresets } from '@copilot/common/hooks/smartReply/smartReplyPromptPresets/useSmartReplyPromptPresetContext';
import { SmartReplyButton } from './smartReplyButton';
import { SmartReplyPromptCategory } from './promptMenu/promptMenuTypes';
import { getOperationStatus } from '@copilot/common/hooks/smartReply/smartReplyUtils';

const StyledAlert = styled(Alert)`
    border: 0;
    padding: 2px 12px;
    background-color: ${(props) => props.theme['@Gray/3']};

    .${(props) => props.theme['@ant-prefix']}-alert-message, svg {
        color: ${(props) => props.theme['@Gray/7']};
    }
`;

type SmartReplyProps = {
    /* Whether the ai assistant is currently active */
    isActive: boolean;
    /* Whether the user can regenerate */
    canRegenerate: boolean;
    /* Callback to retry an AI query */
    onRegenerate: () => void;
    /* Update whether the AI assistant is currently active */
    setIsActive: (update: boolean) => void;
    /* Callback to send AI query */
    onWrite: WriteSmartReplyCallback;
    /* Data returned from a ChatGPT query */
    data: WriteSmartReplyQuery | EditSmartReplyQuery;
    /* Callback to send AI query */
    onEdit: (
        prompt: string,
        message?: string,
        includePersonalizedInsights?: boolean
    ) => Promise<void>;
    /* Whether or not the parents text area has a message */
    hasMessage: boolean;
    /* Whether AI Assistant is disabled */
    disabled?: boolean;
    /* The prompt category that currently applies to the conversation */
    smartReplyPromptCategory?: SmartReplyPromptCategory;
    /* Whether the user can use communication styles */
    canUseCommunicationStyles: boolean;
};
/**
 * Component providing Smart Reply
 * @constructor
 */
export function SmartReply({
    isActive,
    setIsActive,
    onWrite,
    onEdit,
    canRegenerate,
    onRegenerate,
    data,
    hasMessage,
    disabled,
    smartReplyPromptCategory,
    canUseCommunicationStyles,
}: SmartReplyProps) {
    const [isStatusVisible, setStatusVisibility] = useState<boolean>(false);
    const { operationStatus, operationMessage } = getOperationStatus(data);
    const { getByKey: getPromptByKey } = useSmartReplyPromptPresets();
    useEffect(() => {
        if (data?.loading) setStatusVisibility(true);
    }, [data?.loading]);

    useEffect((): (() => void) | void => {
        if (data?.data) {
            const delayId = delay(() => setStatusVisibility(false), 5000);
            return () => clearTimeout(delayId);
        }
    }, [data?.data]);

    /**
     * Function called when user creates a custom prompt
     * @param prompt
     */
    function onSubmitCustomPrompt(
        prompt: string,
        isEditing: boolean,
        includeCommunicationStyles: boolean
    ) {
        setIsActive(false);
        isEditing
            ? onEdit(CUSTOM_PROMPT_KEY, prompt, includeCommunicationStyles).catch((err) =>
                  console.error(err)
              )
            : onWrite(CUSTOM_PROMPT_KEY, prompt, includeCommunicationStyles).catch((err) =>
                  console.error(err)
              );
    }

    /**
     * Function called when user selects a preset prompt
     * @param promptKey
     */
    function onSelectPreset(promptKey: string, includeCommunicationStyles: boolean) {
        setIsActive(false);
        const prompt = getPromptByKey(promptKey);
        if (isUndefined(prompt)) throw new Error(`Prompt with key ${promptKey} not found`);
        prompt.action == 'EDIT'
            ? onEdit(prompt.id, undefined, includeCommunicationStyles).catch((err) =>
                  console.error(err)
              )
            : onWrite(prompt.id, undefined, includeCommunicationStyles).catch((err) =>
                  console.error(err)
              );
    }

    return (
        <>
            {isStatusVisible && (
                <StatusIndicator status={operationStatus}>{operationMessage}</StatusIndicator>
            )}
            {!isStatusVisible && canRegenerate && (
                <StyledAlert type="info" message="Review the AI response for accuracy." showIcon />
            )}
            {!data?.loading && (
                <SmartReplyButton
                    hasMessage={hasMessage}
                    isActive={isActive}
                    setIsActive={setIsActive}
                    canRegenerate={canRegenerate}
                    onRegenerate={onRegenerate}
                    onSubmitCustomPrompt={onSubmitCustomPrompt}
                    onSelectPreset={onSelectPreset}
                    disabled={disabled}
                    smartReplyPromptCategory={smartReplyPromptCategory}
                    canUseCommunicationStyles={canUseCommunicationStyles}
                />
            )}
        </>
    );
}
